// Tab Servicios del cliente 360: split 50/50 con cards resumen (izq.)
// y mapa de pagos geolocalizados (der.). Este slice solo recompone la
// jerarquía visual de lista/card; no cambia contratos de datos ni del mapa.
import { useState } from 'react';
import { Button, Checkbox, Col, Row, Spin, Typography, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { EmptyState } from '../../../components/EmptyState';
import { useClienteServicios } from '../api/useClientes';
import { useHasPermission } from '../../../auth/useHasPermission';
import { resumenEstados, precioTotalActivos } from '../cliente-servicios';
import { ServicioCard } from './ServicioCard';
import { ServicioCreateModal } from '../../servicios/components/ServicioCreateModal';
import { MapaPagosCliente } from './MapaPagosCliente';
import { useT } from '../../../locales';
import { Permission } from '@aid/shared';

const { Text } = Typography;
const LIMITE_SERVICIOS = 10;

interface ClienteServiciosListProps {
  clienteId: number;
}

function totalLabel(total: number, t: ReturnType<typeof useT>): string {
  return t(
    total === 1 ? 'pages.clientes.servicios.totalOne' : 'pages.clientes.servicios.totalOther',
    { total },
  );
}

function activosLabel(activos: number, t: ReturnType<typeof useT>): string {
  return t(
    activos === 1 ? 'pages.clientes.servicios.activeOne' : 'pages.clientes.servicios.activeOther',
    { total: activos },
  );
}

export function ClienteServiciosList({ clienteId }: ClienteServiciosListProps) {
  const t = useT();
  const { token } = theme.useToken();
  const canCreate = useHasPermission(Permission.SERVICIOS_CREATE);
  const [createOpen, setCreateOpen] = useState(false);
  // BAJA filter: off by default — the list only shows active services and the
  // hidden BAJA count lives in the checkbox/empty-state notice (bajasOcultas).
  const [mostrarBajas, setMostrarBajas] = useState(false);

  const { data, isLoading, error } = useClienteServicios(clienteId, {
    limit: LIMITE_SERVICIOS,
    offset: 0,
    incluirBajas: mostrarBajas,
  });

  const servicios = data?.data ?? [];
  const total = data?.total ?? 0;
  const bajasOcultas = data?.bajasOcultas ?? 0;
  const resumen = resumenEstados(servicios);
  const totalActivos = precioTotalActivos(servicios);
  const activos = resumen.porEstado.find((estado) => estado.clave === 'activo')?.cantidad ?? 0;
  const resumenPartes = [totalLabel(resumen.total, t), activosLabel(activos, t)];

  if (totalActivos) {
    resumenPartes.push(t('pages.clientes.servicios.monthlyTotal', { monto: totalActivos }));
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title={t('pages.clientes.servicios.errorLoad')}
        description={t('pages.clientes.servicios.errorLoadDesc')}
      />
    );
  }

  // Empty state: with the BAJA filter off, a client whose services are all
  // deactivated shows no rows but has a hidden BAJA count — invite the user
  // to reveal them instead of claiming there are no services at all.
  if (total === 0 && !isLoading && !error) {
    if (bajasOcultas > 0) {
      return (
        <EmptyState
          title={t('pages.clientes.servicios.emptySinActivos')}
          description={t(
            bajasOcultas === 1
              ? 'pages.clientes.servicios.dadosBajaOne'
              : 'pages.clientes.servicios.dadosBajaOther',
            { count: bajasOcultas },
          )}
          action={
            <Button type="primary" size="small" onClick={() => setMostrarBajas(true)}>
              {t('pages.clientes.servicios.showBajasAction')}
            </Button>
          }
        />
      );
    }
    return (
      <>
        <EmptyState
          title={t('pages.clientes.sinServicios')}
          description={t('pages.clientes.sinServiciosDesc')}
          action={
            canCreate ? (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
              >
                {t('pages.clientes.agregarServicio')}
              </Button>
            ) : undefined
          }
        />
        <ServicioCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          preselectedClienteId={clienteId}
        />
      </>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Text
              strong
              style={{
                fontSize: 14,
                lineHeight: 1.6,
                color: token.colorText,
                textWrap: 'pretty',
              }}
            >
              {resumenPartes.join(' · ')}
            </Text>
            {bajasOcultas > 0 && (
              <Checkbox checked={mostrarBajas} onChange={(e) => setMostrarBajas(e.target.checked)}>
                {t(
                  bajasOcultas === 1
                    ? 'pages.clientes.servicios.showBajaOne'
                    : 'pages.clientes.servicios.showBajaOther',
                  { count: bajasOcultas },
                )}
              </Checkbox>
            )}
          </div>
          {canCreate && (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              {t('pages.clientes.agregarServicio')}
            </Button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
          {servicios.map((servicio) => (
            <ServicioCard key={servicio.id} servicio={servicio} />
          ))}
        </div>
      </Col>
      <Col xs={24} lg={10}>
        <MapaPagosCliente clienteId={clienteId} />
      </Col>
      <ServicioCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        preselectedClienteId={clienteId}
      />
    </Row>
  );
}
